import { useQuery } from '@tanstack/react-query';
import { SwiftClient } from '@velocity-exchange/common';
import { createQueryKey } from 'src/utils/query/createQueryKey';

// Poll less often while healthy, faster while unhealthy to re-enable swift asap.
const HEALTHY_REFETCH_MS = 120_000;
const UNHEALTHY_REFETCH_MS = 20_000;

const swiftServerHealthQueryKey = createQueryKey('swiftServerHealth');

/**
 * Mount ONCE (in `useAppSetup`). Owns the single app-wide swift-server health
 * poll and writes the result into the shared QueryClient cache. Consumers read
 * that cached value via `useSwiftServerHealthCheck`, so there is exactly one
 * polling loop regardless of how many consumers are mounted (replaces the
 * previous react-singleton-hook wrapper).
 */
export const useSyncSwiftServerHealth = () => {
	useQuery({
		queryKey: swiftServerHealthQueryKey,
		queryFn: () => SwiftClient.isSwiftServerHealthy(),
		// keep polling regardless of tab focus (matches the previous behaviour)
		refetchInterval: (query) =>
			query.state.data === false ? UNHEALTHY_REFETCH_MS : HEALTHY_REFETCH_MS,
		refetchIntervalInBackground: true,
		refetchOnWindowFocus: false,
		retry: false,
	});
};

/**
 * Read the shared swift-server health result. `enabled: false` means consumers
 * only subscribe to the value written by `useSyncSwiftServerHealth` — they never
 * start their own fetch or refetch interval, so mounting more consumers does not
 * add polling loops or extra requests.
 *
 * `data` is `undefined` until the first response, so consumers can distinguish
 * "still loading" from "known unhealthy" — SwiftSettings checks `=== undefined`
 * and SwiftOfflineIndicator checks `!== false`.
 */
export const useSwiftServerHealthCheck = (): boolean | undefined => {
	const { data } = useQuery({
		queryKey: swiftServerHealthQueryKey,
		queryFn: () => SwiftClient.isSwiftServerHealthy(),
		enabled: false,
	});

	return data;
};
